import { useContext, useEffect, useState } from 'react';
import apiFetch from '../../context/api';
import { UserContext } from '../../context/UserContext';
import { MERMA_TOLERANCIA_G } from '../helpers/products';

/**
 * Tolerancia de báscula EFECTIVA del club (merma máxima por línea, en gramos
 * absolutos): la fijada en Configuración (venues.merma_tolerancia_g) o el
 * default del sistema (0,10 g) si el club no tocó nada. El backend valida con
 * la misma resolución (merma_tolerancia_de) — el FE avisa, el backend manda.
 *
 * Caché por módulo: la usan componentes que se montan por tarjeta
 * (ProductGridCart) y pantallas enteras (Sala, Barra) — un solo GET por venue
 * y sesión, compartido. Si el GET falla se responde el default: una pantalla
 * operativa no se bloquea por un ajuste.
 */
const cache = new Map(); // venueID → Promise<number>

function fetchTolerancia(venueID, token) {
  if (!cache.has(venueID)) {
    // apiFetch, no fetch a pelo: antepone getApiBase(). En prod el FE y el API
    // son dominios DISTINTOS (clubsagent.ai / api.clubsagent.ai) — con fetch
    // relativo la petición iba al SPA, recibía index.html y el catch devolvía
    // el default para siempre (hallazgo del code review 23-08: el ajuste del
    // club no aplicaba en prod y nadie veía un error).
    const promesa = apiFetch(`/v1/venues/${venueID}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => {
        const g = Number(v?.merma_tolerancia_g);
        return Number.isFinite(g) && g > 0 ? g : MERMA_TOLERANCIA_G;
      })
      .catch(() => MERMA_TOLERANCIA_G);
    cache.set(venueID, promesa);
  }
  return cache.get(venueID);
}

/** Invalida la caché (la usa Configuración al guardar una tolerancia nueva). */
export function invalidarMermaTolerancia(venueID) {
  cache.delete(venueID);
}

export default function useMermaTolerancia() {
  const ctx = useContext(UserContext) || [];
  const token = ctx[0];
  const venueID = ctx[3];
  const [toleranciaG, setToleranciaG] = useState(MERMA_TOLERANCIA_G);

  useEffect(() => {
    // Sin red (jsdom en tests sin mock) o sin sesión: el default, sin romper la pantalla.
    if (!token || !venueID || typeof fetch !== 'function') return undefined;
    let vivo = true;
    fetchTolerancia(venueID, token).then((g) => {
      if (vivo) setToleranciaG(g);
    });
    return () => {
      vivo = false;
    };
  }, [token, venueID]);

  return toleranciaG;
}
